import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Search, MessageSquare, Boxes, Network, GitPullRequestArrow, BookOpen,
  Plug, LayoutGrid, History, Bookmark, Settings, Hash, FileSearch, Sparkles,
} from "lucide-react";

type Cmd = {
  id: string;
  label: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  group: string;
  shortcut?: string;
  run: () => void;
};

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setIndex(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const close = () => setOpen(false);

  const commands: Cmd[] = useMemo(() => {
    const nav = (label: string, to: string, icon: Cmd["icon"]): Cmd => ({
      id: `nav-${to}`, label, hint: to, icon, group: "Navigate",
      run: () => { navigate({ to }); close(); },
    });
    return [
      nav("Copilot", "/copilot", MessageSquare),
      nav("Parts Catalog", "/parts", Boxes),
      nav("BOM Explorer", "/bom", Network),
      nav("Changes", "/changes", GitPullRequestArrow),
      nav("Knowledge Base", "/knowledge", BookOpen),
      nav("Teamcenter Integration", "/integration", Plug),
      nav("Integrations Hub", "/hub", LayoutGrid),
      nav("Actions Audit Log", "/audit", History),
      nav("Saved Insights", "/insights", Bookmark),
      nav("Settings", "/settings", Settings),
      { id: "find-part", label: "Find a part by ID or name", hint: "Search parts catalog", icon: Hash, group: "Find", run: close },
      { id: "find-bom", label: "Open a BOM", hint: "Browse assemblies", icon: Network, group: "Find", run: close },
      { id: "find-ecn", label: "Look up an ECN", hint: "Engineering change", icon: GitPullRequestArrow, group: "Find", run: close },
      { id: "find-kb", label: "Search knowledge base", hint: "Standards & specs", icon: FileSearch, group: "Find", run: close },
      { id: "ask-copilot", label: "Ask copilot a question…", hint: "Natural language", icon: Sparkles, group: "Ask Copilot", shortcut: "↵", run: close },
    ];
  }, [navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => c.label.toLowerCase().includes(q) || c.hint?.toLowerCase().includes(q));
  }, [commands, query]);

  const grouped = useMemo(() => {
    const groups: Record<string, Cmd[]> = {};
    filtered.forEach((c) => { (groups[c.group] ||= []).push(c); });
    return groups;
  }, [filtered]);

  useEffect(() => { setIndex(0); }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIndex((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); filtered[index]?.run(); }
  };

  if (!open) return null;

  let cursor = -1;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" onClick={close}>
      <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" />
      <div
        className="relative z-10 w-full max-w-xl overflow-hidden rounded-md border border-border-strong bg-popover shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search or run a command"
            className="h-10 w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">esc</kbd>
        </div>

        <div className="max-h-[360px] overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-[13px] text-muted-foreground">No results</div>
          )}
          {Object.entries(grouped).map(([group, items]) => (
            <div key={group} className="px-1 py-1">
              <div className="px-2 py-1 text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
                {group}
              </div>
              {items.map((c) => {
                cursor++;
                const active = cursor === index;
                const Icon = c.icon;
                return (
                  <button
                    key={c.id}
                    onMouseEnter={() => setIndex(filtered.indexOf(c))}
                    onClick={() => c.run()}
                    className={`flex w-full items-center gap-2.5 rounded-sm px-2 py-1.5 text-left text-[13px] transition-colors ${
                      active ? "bg-sidebar-active text-foreground" : "text-foreground/90 hover:bg-sidebar-hover"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{c.label}</span>
                    {c.hint && <span className="text-[11px] text-muted-foreground">{c.hint}</span>}
                    {active && c.shortcut && (
                      <kbd className="rounded border border-border bg-background px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {c.shortcut}
                      </kbd>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
